import Redactor from '../../redactor.esm.min.js'
import { registerToolbarButton } from '../registerButton'

class MpLineHeightPlugin {
    static defaultOptions = {
        input: true
    }

    static defaultLang = {
        en: {
            mpLineHeight: {
                title: 'Line Height'
            }
        }
    }

    constructor(app, options = {}) {
        this.app = app
        this.options = options
        this.currentlySelectedLineHeight = ''
        this.defaultTitle = 'Line Height'
        this.buttonDropDownIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.6098 14.609C10.9358 14.2832 11.4644 14.2833 11.7905 14.609C12.1166 14.9351 12.1166 15.4646 11.7905 15.7906L8.59027 18.9899C8.43372 19.1464 8.22181 19.2349 8.00043 19.235C7.77897 19.235 7.5662 19.1465 7.4096 18.9899L4.20941 15.7906C3.88332 15.4646 3.88332 14.9351 4.20941 14.609C4.5355 14.2829 5.06496 14.2829 5.39105 14.609L7.99945 17.2184L10.6098 14.609Z" fill="black"/>
<path d="M7.99985 4.76483C8.22142 4.76488 8.4341 4.85318 8.59067 5.00995L11.7909 8.21405C12.1164 8.54036 12.116 9.06895 11.7899 9.39471C11.4636 9.72056 10.9351 9.7199 10.6092 9.39374L7.99985 6.78046L5.39048 9.39374C5.06465 9.71997 4.53612 9.72043 4.20981 9.39471C3.88358 9.06889 3.88312 8.54036 4.20884 8.21405L7.40903 5.00995L7.47056 4.95428C7.61916 4.83247 7.80601 4.76483 7.99985 4.76483Z" fill="black"/>
<path d="M7.16504 18.3998V5.60004C7.16504 5.13888 7.53884 4.76508 8 4.76508C8.46116 4.76508 8.83496 5.13888 8.83496 5.60004V18.3998C8.83496 18.861 8.46116 19.2348 8 19.2348C7.53884 19.2348 7.16504 18.861 7.16504 18.3998Z" fill="black"/>
<path d="M19.2002 6.96503C19.6613 6.96513 20.0352 7.33889 20.0352 7.79999C20.0352 8.26108 19.6613 8.63484 19.2002 8.63495H15C14.5388 8.63495 14.165 8.26115 14.165 7.79999C14.165 7.33883 14.5388 6.96503 15 6.96503H19.2002Z" fill="black"/>
<path d="M19.2002 11.165C19.6613 11.1651 20.0352 11.5389 20.0352 12C20.0352 12.4611 19.6613 12.8349 19.2002 12.835H15C14.5388 12.835 14.165 12.4612 14.165 12C14.165 11.5388 14.5388 11.165 15 11.165H19.2002Z" fill="black"/>
<path d="M19.2002 15.3651C19.6613 15.3652 20.0352 15.7389 20.0352 16.2C20.0352 16.6611 19.6613 17.0349 19.2002 17.035H15C14.5388 17.035 14.165 16.6612 14.165 16.2C14.165 15.7389 14.5388 15.3651 15 15.3651H19.2002Z" fill="black"/>
</svg>`
        this._onSelectionChange = () => {
            setTimeout(() => this._updateButtonContent(), 10)
        }
    }

    start() {
        registerToolbarButton(this.app, 'mpLineHeight', this._createToolbarButton())
        this.app.on('click', this._onSelectionChange)
        this.app.on('key:up', this._onSelectionChange)
        this.app.on('editor:focus', this._onSelectionChange)
    }

    stop() {
        this.app.off('click', this._onSelectionChange)
        this.app.off('key:up', this._onSelectionChange)
        this.app.off('editor:focus', this._onSelectionChange)
    }

    _createToolbarButton() {
        return {
            title: 'Line Height',
            icon: this.buttonDropDownIcon,
            position: { before: 'moreinline' },
            command: 'mpLineHeight.popup'
        }
    }

    _normalizeLineHeight(value) {
        if (!value || value === 'normal') {
            return ''
        }

        const normalized = String(value).trim().replace(/;$/, '')
        if (normalized.endsWith('%')) {
            return normalized
        }

        const numericValue = parseFloat(normalized)
        if (!isNaN(numericValue)) {
            if (numericValue <= 3) {
                return Math.round(numericValue * 100) + '%'
            }

            return Math.round(numericValue) + '%'
        }

        return normalized
    }

    _getLineHeightFromBlock(instance) {
        if (!instance) return ''

        const styles = instance.getStyles ? instance.getStyles() : null
        if (styles && styles.lineHeight) {
            return this._normalizeLineHeight(styles.lineHeight)
        }

        const el = instance.getEl && instance.getEl()
        if (!el) return ''
        const view = (el.ownerDocument && el.ownerDocument.defaultView) || window
        return this._normalizeLineHeight(view.getComputedStyle(el).lineHeight)
    }

    _getLineHeightFromInline() {
        const selection = this.app.getService('selection')
        const inlines = selection.getNodes({ types: 'inline' }) || []
        if (inlines.length === 0) return ''

        const rawValue = inlines[inlines.length - 1].getAttribute('style')
        if (!rawValue) return ''

        const match = rawValue.match(/line-height:\s*([\d.]+%?|normal)/i)
        if (!match) return ''

        return this._normalizeLineHeight(match[1].trim())
    }

    _updateButtonContent() {
        let selectedLineHeight = ''

        if (this.app.blocks.is()) {
            const blocks = this.app.blocks.get({ editable: true })
            if (blocks.length > 0) {
                selectedLineHeight = this._getLineHeightFromBlock(blocks[0])
            }
        } else {
            selectedLineHeight = this._getLineHeightFromBlock(this.app.block.get())
        }

        if (!selectedLineHeight) {
            selectedLineHeight = this._getLineHeightFromInline()
        }

        this.currentlySelectedLineHeight = selectedLineHeight || this.defaultTitle
    }

    _blockFromNode(node) {
        let el = node && node.nodeType === 1 ? node : node && node.parentElement
        while (el) {
            const block = this.app.blocks.findByEl(el)
            if (block) return block
            el = el.parentElement
        }
        return null
    }

    _getBlocksToStyle() {
        if (this.app.blocks.is()) {
            return this.app.blocks.get({ editable: true })
        }

        const instance = this.app.block.get()
        if (instance) return [instance]

        const selection = this.app.getService('selection')
        if (!selection.getRange()) return []

        const blocks = []
        const seen = new Set()
        const nodes = selection.getNodes({ tags: ['p', 'h1', 'h2', 'h3', 'h4', 'div', 'li'] }) || []

        nodes.forEach((node) => {
            const block = this._blockFromNode(node)
            if (!block || seen.has(block)) return
            seen.add(block)
            blocks.push(block)
        })

        return blocks
    }

    _removeInlineLineHeight() {
        const selection = this.app.getService('selection')
        const nodes = selection.getNodes({ types: 'inline' }) || []
        nodes.forEach((el) => {
            if (!el || el.nodeType !== 1 || !el.style || !el.style.lineHeight) return
            el.style.removeProperty('line-height')
            if (!el.getAttribute('style') && el.tagName === 'SPAN' && !el.className) {
                const parent = el.parentNode
                if (!parent) return
                while (el.firstChild) parent.insertBefore(el.firstChild, el)
                parent.removeChild(el)
            }
        })
    }

    popup(_params, { button, el } = {}) {
        this.app.saveSelection()
        this._updateButtonContent()

        const currentLineHeight = this._normalizeLineHeight(this.currentlySelectedLineHeight)
        const lineHeightOptions = [
            { title: '<div>Condensed</div>', value: '100%' },
            { title: '<div>Snug</div>', value: '125%' },
            { title: '<div>Normal</div>', value: '150%' },
            { title: '<div>Relaxed</div>', value: '175%' },
            { title: '<div>Double</div>', value: '200%' }
        ]

        const items = lineHeightOptions.map((option) => ({
            title: option.title,
            command: 'mpLineHeight.set',
            active: currentLineHeight === option.value,
            params: {
                tag: 'span',
                style: {
                    'line-height': option.value
                }
            }
        }))

        this.app.dropdown.create('mpLineHeight', { items: items })
        this.app.dropdown.open(button || el)
    }

    set(params = {}) {
        const styles = params.styles || params.style || {}
        const lineHeight = styles['line-height']
        this.currentlySelectedLineHeight = lineHeight
        this.app.dropdown.close()

        this._removeInlineLineHeight()

        const blocks = this._getBlocksToStyle()
        if (blocks.length > 0) {
            blocks.forEach((block) => this._setBlockLineHeight(block, lineHeight))
            return
        }

        this.app.inline.set({ tag: 'span', styles })
    }

    _setBlockLineHeight(instance, lineHeight) {
        if (!instance) return

        const el = instance.getEl && instance.getEl()
        if (instance.isType && instance.isType('list') && el) {
            el.querySelectorAll('li').forEach((li) => {
                const item = this.app.blocks.findByEl(li)
                if (item && item.setStyles) item.setStyles({ 'line-height': lineHeight })
                else li.style.lineHeight = lineHeight
            })
        }

        if (instance.setStyles) instance.setStyles({ 'line-height': lineHeight })
    }
}

Redactor.addPlugin('mpLineHeight', MpLineHeightPlugin)
